import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import {
    type ChainActivityResponse,
    type GetAddressActivityQueryParamOpts,
    type GetMultiChainBalanceQueryParamOpts,
    type GetMultiChainMultiAddressTransactionsParamOtps,
    type MultiChainBalanceResponse,
    type MultiChainMultiAddressTransactionsResponse,
} from "../utils/types/AllChainService.types";
import { type GoldRushResponse } from "../utils/types/Generic.types";

/**
 * Cross Chain API
 *
 */
export class AllChainsService {
    constructor(private execution: Execution) {}

    /**
     *
     * Commonly used to locate chains which an address is active on with a single API call.
     *
     * **Credit Cost**: 0.5 per call
     *
     * @param {string} walletAddress - The requested wallet address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetAddressActivityQueryParamOpts} queryParamOpts
     *   - `testnets`: Set to true to include testnets with activity in the response. By default, it's set to `false` and only returns mainnet activity.
     *
     */
    public async getAddressActivity(
        walletAddress: string,
        queryParamOpts?: GetAddressActivityQueryParamOpts
    ): Promise<GoldRushResponse<ChainActivityResponse>> {
        const endpoint = endpointGenerator(
            `address/${walletAddress}/activity`,
            [
                {
                    key: "testnets",
                    value: queryParamOpts?.testnets,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<ChainActivityResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((activityItem) => ({
                          ...activityItem,
                          last_seen_at: activityItem.last_seen_at
                              ? new Date(activityItem.last_seen_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<ChainActivityResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to get transactions cross chains and addresses.
     *
     * @param {Chain[]} chains - An array of the chain names or IDs to retrieve transactions from. Defaults to all foundational chains.
     * @param {string[]} addresses - An array of addresses for which transactions are fetched. Does not support name resolution.
     * @param {number} limit - Number of transactions to return per page, up to the default max of 100 items.
     * @param {string} before - Pagination cursor pointing to fetch transactions before a certain point.
     * @param {string} after - Pagination cursor pointing to fetch transactions after a certain point.
     * @param {boolean} withLogs - Whether to include raw logs in the response.
     * @param {boolean} withDecodedLogs - Whether to include decoded logs in the response.
     * @param {Quote | CryptocurrencyQuote} quoteCurrency - The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, `GBP`, `BTC` and `ETH`.
     *
     */
    public async getMultiChainMultiAddressTransactions(
        queryParamOpts?: GetMultiChainMultiAddressTransactionsParamOtps
    ): Promise<GoldRushResponse<MultiChainMultiAddressTransactionsResponse>> {
        const endpoint = endpointGenerator(`allchains/transactions`, [
            {
                key: "chains",
                value: queryParamOpts?.chains as string[],
            },
            {
                key: "addresses",
                value: queryParamOpts?.addresses,
            },
            {
                key: "limit",
                value: queryParamOpts?.limit,
            },
            {
                key: "before",
                value: queryParamOpts?.before,
            },
            {
                key: "after",
                value: queryParamOpts?.after,
            },
            {
                key: "with-logs",
                value: queryParamOpts?.withLogs,
            },
            {
                key: "with-decoded-logs",
                value: queryParamOpts?.withDecodedLogs,
            },
            {
                key: "quote-currency",
                value: queryParamOpts?.quoteCurrency,
            },
        ]);

        const parseData = (
            data: GoldRushResponse<MultiChainMultiAddressTransactionsResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((txItem) => ({
                          ...txItem,
                          value: bigIntParser(txItem.value),
                          fees_paid: bigIntParser(txItem.fees_paid),
                          block_signed_at: txItem.block_signed_at
                              ? new Date(txItem.block_signed_at)
                              : null,
                          log_events: txItem.log_events
                              ? txItem.log_events.map((logItem) => ({
                                    ...logItem,
                                    block_signed_at: logItem.block_signed_at
                                        ? new Date(logItem.block_signed_at)
                                        : null,
                                }))
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<MultiChainMultiAddressTransactionsResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Fetch paginated spot & historical native and token balances for a single address on up to 10 EVM chains with one API call.
     *
     * **Credit Cost**: 2.5 per call
     *
     * @param {string} walletAddress - The requested wallet Address.
     * @param {GetMultiChainBalanceQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`,  `GBP`, `BTC`, and `ETH`.
     *   - `chains`: Array chain ids or chain names to query. Limited to 10 chains.
     *   - `limit`: The number of items to return. Default is 100.
     *   - `timestamp`: The timestamp to query balances at. If omitted, the latest balances are returned.
     *   - `cursorBefore`: The cursor of previous page.
     */
    public async getMultiChainBalances(
        walletAddress: string,
        queryParamOpts?: GetMultiChainBalanceQueryParamOpts
    ): Promise<GoldRushResponse<MultiChainBalanceResponse>> {
        const endpoint = endpointGenerator(
            `allchains/address/${walletAddress}/balances`,
            [
                {
                    key: "chains",
                    value: queryParamOpts?.chains as string[],
                },
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "limit",
                    value: queryParamOpts?.limit,
                },
                {
                    key: "cutoff-timestamp",
                    value: queryParamOpts?.cutoffTimestamp,
                },
                {
                    key: "before",
                    value: queryParamOpts?.before,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<MultiChainBalanceResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          balance_24h: bigIntParser(balanceItem.balance_24h),
                          last_transferred_at: balanceItem.last_transferred_at
                              ? new Date(balanceItem.last_transferred_at)
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<MultiChainBalanceResponse>(
            endpoint,
            parseData
        );
    }
}
